/**
 * Create the store with dynamic reducers
 */

import { applyMiddleware, compose, createStore } from "redux";
import createSagaMiddleware from "redux-saga";
import createReducer from "./reducers";
import * as Sentry from "@sentry/browser";
import { Integrations } from "@sentry/tracing";
import { createLogger } from "redux-logger";
import { Iterable } from "immutable";
import createSentryMiddleware from "redux-sentry-middleware";
import { sentryOptions } from "./utils/sentryOptions";
import * as env from "./env";
import { isSupportedBrowser } from ".";
import apiMiddleware from "./api/redux/middleware";
import { consoleError } from "./utils/log";

fetch("/sentryDSN")
  .then((res) => res.text())
  .then((dsn) => {
    Sentry.init({
      dsn,
      environment: env.getEnv(),
      release: env.getVersion(),
      enabled: env.isLive() && isSupportedBrowser,
      autoSessionTracking: true,
      integrations: [new Integrations.BrowserTracing()],
      tracesSampleRate: 0.5,
      ignoreErrors: [
        "502 Bad Gateway",
        "Lock wait timeout exceeded",
        "Non-Error promise rejection captured with value: Timeout",
        "503 Service Temporarily Unavailable",
        "Generator is already running",
        "ChunkLoadError",
        "replaceAll is not a function",
        "Non-Error exception captured",
        "Non-Error promise rejection captured",
        "Failed to fetch",
        "_leaflet_pos",
      ],
      beforeSend(event) {
        // filter out UnhandledRejection errors that have no information
        if (
          event !== undefined &&
          event.exception !== undefined &&
          event.exception.values !== undefined &&
          event?.exception?.values?.length === 1
        ) {
          const e = event.exception.values[0];
          if (e.value.match(/Missing required parameters: sitekey/gms))
            return null;
          if (e.value.match(/Loading .* chunk [0-9]* failed.*/gms)) {
            // Caching issue. New version available.
            window.location.reload();
            return null;
          }
          if (
            e.type === "UnhandledRejection" &&
            e.value === "Non-Error promise rejection captured with value: "
          ) {
            return null;
          }
        }
        return event;
      },
    });
  });

const sagaMiddleware = createSagaMiddleware({
  // Watchers are attached children of appSaga: one uncaught error cancels every
  // sibling, leaving the app silently unresponsive until a reload.
  onError: (error, errorInfo) => {
    consoleError("Uncaught saga error — the saga tree has been torn down", error, errorInfo);
    Sentry.captureException(error, { extra: errorInfo });
  },
});
const sentryMiddleware = createSentryMiddleware(Sentry, sentryOptions);

let store;

export const getStore = () => store;

const stateTransformer = (state) => {
  if (Iterable.isIterable(state)) return state.toJS();
  else return state;
};

const logger = createLogger({
  stateTransformer: (state) => {
    let newState = {};

    for (var i of Object.keys(state)) {
      if (Iterable.isIterable(state[i])) {
        newState[i] = state[i].toJS();
      } else {
        newState[i] = state[i];
      }
    }

    return newState;
  },
});

export default function configureStore(initialState = {}) {
  const middlewares = [sentryMiddleware, sagaMiddleware, apiMiddleware].concat(
    env.isLive() ? [] : [logger],
  );

  const enhancers = [applyMiddleware(...middlewares)];

  store = createStore(createReducer(), initialState, compose(...enhancers));

  const appSaga = require("./redux/sagas/app.saga").default;
  sagaMiddleware.run(appSaga);

  // Extensions
  store.runSaga = sagaMiddleware.run;
  store.injectedReducers = {}; // Reducer registry
  store.injectedSagas = {}; // Saga registry

  // Make reducers hot reloadable, see http://mxs.is/googmo
  /* istanbul ignore next */
  if (module.hot) {
    module.hot.accept("./reducers", () => {
      store.replaceReducer(createReducer(store.injectedReducers));
    });
  }

  return store;
}
